<template>
  <div class="container my-3">
    <div class="row">
      <div class="col-12 col-md-9 order-2 order-md-1">
        <CustomAlert type="secondary" title=""
          ><span class="h3 fw-bold"
            >Lab 2: Site Selection and Sampling</span
          ></CustomAlert
        >
        <CustomAlert
          ><p>Fill in each field of the lab form.</p>
          <p>
            Click <span class="badge bg-primary">Save</span> often to save your
            progress. Your session will expire and your data lost if you switch
            browsers or devices without clicking Save first.
          </p>
          <p class="mb-0">
            If you encounter any difficulty, use the
            <strong>Support</strong> form in the navigation.
          </p></CustomAlert
        >
        <form @submit.stop>
          <DiscussionQuestions
            :labContent="labContent"
            :title="labContent.discussionQuestionsPartOneTitle"
            @changeAnswers="handleDiscussionChange($event, 'discussionAnswers')"
            :discussionAnswers="formData.discussionAnswers"
            :exportMode="exportMode"
          />

          <!-- visitingSiteImages -->
          <DynamicCard :title="labContent.discussionQuestionsPartTwoTitle">
            <div class="card-body">
              <div
                v-for="(
                  directions, index
                ) in labContent.visitingSitePhotoDirections"
                :key="index"
              >
                <div class="row">
                  <div class="col">
                    <p class="form-label text-start fw-bold m-3">
                      {{ index + 1 }}. {{ directions.text }}
                    </p>

                    <ul v-if="directions.stepsList">
                      <li
                        v-for="step in directions.stepsList"
                        :key="step"
                        class="lead text-start"
                      >
                        {{ step }}
                      </li>
                    </ul>
                  </div>
                  <div v-if="index === 2">
                    <div
                      v-for="image in labContent.northSouthEastWestPhotoDirections"
                      :key="image.imageKey"
                    >
                      <SketchUpload
                        :sketchUploadHeaders="image"
                        :imageURL="findImageByKey(image.imageKey)"
                        :formId="labContent.id"
                        @changeImage="handleImageChange"
                        :exportMode="exportMode"
                      />
                    </div>
                  </div>
                  <div v-else>
                    <SketchUpload
                      :sketchUploadHeaders="directions"
                      :imageURL="findImageByKey(directions.imageKey)"
                      :formId="labContent.id"
                      @changeImage="handleImageChange"
                      :exportMode="exportMode"
                    />
                  </div>
                </div>
                <div
                  v-if="
                    index + 1 < labContent.visitingSitePhotoDirections.length
                  "
                  class="mb-5"
                >
                  <hr />
                </div>
              </div>
            </div>
          </DynamicCard>
          <DynamicCard :title="labContent.discussionQuestionsPartThreeTitle">
            <div class="text-start">
              The process of science hinges on curiosity -- making close
              observations and be willing to ask questions about how those
              observations fit with existing knowledge. You must ask a question
              where prompted in Question 2 of each subsection below. "I have no
              questions" is not an acceptable answer.
            </div>
          </DynamicCard>
          <DiscussionQuestions
            :title="labContent.discussionQuestionsClimate.title"
            :labContent="labContent.discussionQuestionsClimate"
            @changeAnswers="
              handleDiscussionChange($event, 'discussionClimateAnswers')
            "
            :discussionAnswers="formData.discussionClimateAnswers"
            :exportMode="exportMode"
          />
          <DiscussionQuestions
            :title="labContent.discussionQuestionsOrganisms.title"
            :labContent="labContent.discussionQuestionsOrganisms"
            @changeAnswers="
              handleDiscussionChange($event, 'discussionOrganismAnswers')
            "
            :discussionAnswers="formData.discussionOrganismAnswers"
            :exportMode="exportMode"
          />
          <DiscussionQuestions
            :title="labContent.discussionQuestionsTopology.title"
            :labContent="labContent.discussionQuestionsTopology"
            @changeAnswers="
              handleDiscussionChange($event, 'discussionTopologyAnswers')
            "
            :discussionAnswers="formData.discussionTopologyAnswers"
            :exportMode="exportMode"
          />
          <DiscussionQuestions
            :title="labContent.discussionQuestionsParentMaterial.title"
            :labContent="labContent.discussionQuestionsParentMaterial"
            @changeAnswers="
              handleDiscussionChange($event, 'discussionParentMaterialAnswers')
            "
            :discussionAnswers="formData.discussionParentMaterialAnswers"
            :exportMode="exportMode"
          />

          <DiscussionQuestions
            :title="labContent.discussionQuestionsTime.title"
            :labContent="labContent.discussionQuestionsTime"
            @changeAnswers="
              handleDiscussionChange($event, 'discussionTimeAnswers')
            "
            :discussionAnswers="formData.discussionTimeAnswers"
            :exportMode="exportMode"
          />
          <DiscussionQuestions
            :title="labContent.discussionQuestionsHumanActivity.title"
            :labContent="labContent.discussionQuestionsHumanActivity"
            @changeAnswers="
              handleDiscussionChange($event, 'discussionHumanActivityAnswers')
            "
            :discussionAnswers="formData.discussionHumanActivityAnswers"
            :exportMode="exportMode"
          />
          <DiscussionQuestions
            :title="labContent.discussionQuestionsSummary.title"
            :labContent="labContent.discussionQuestionsSummary"
            @changeAnswers="
              handleDiscussionChange($event, 'discussionSummaryAnswers')
            "
            :discussionAnswers="formData.discussionSummaryAnswers"
            :exportMode="exportMode"
          />
        </form>
      </div>
      <div class="col-12 col-md-3 order-2 order-md-1 sticky-bottom">
        <LabSidebar
          :formData="formData"
          :exportMode="exportMode"
          :unsavedChanges="unsavedChanges"
          @onSaveEvent="unsavedChanges = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import LabSidebar from "@/components/sidebar/LabSidebar.vue";

import labContent from "@/data/content.json";
import emptyForm from "@/data/formData.js";

import CustomAlert from "@/components/ui/CustomAlert";
import SketchUpload from "@/components/activity/SketchUpload";
import DynamicCard from "@/components/activity/DynamicCard";
import DiscussionQuestions from "@/components/activity/DiscussionQuestions";

export default {
  name: "ActivityView",
  components: {
    CustomAlert,
    LabSidebar,
    SketchUpload,
    DynamicCard,
    DiscussionQuestions
  },
  data() {
    return {
      labContent,
      isReady: false,
      formData: JSON.parse(JSON.stringify(emptyForm)),
      exportMode: false,
      unsavedChanges: false
    };
  },
  computed: {
    ...mapGetters(["getForm"])
  },
  methods: {
    setForm() {
      if (this.getForm.id === undefined) {
        this.formData = JSON.parse(JSON.stringify(emptyForm));
      } else {
        this.formData = this.getForm;
      }
    },
    findImageByKey(key) {
      return this.formData[key];
    },
    handleImageChange(imageData) {
      this.formData[imageData.fieldName] = imageData.imageURL;
      this.unsavedChanges = true;
    },
    handleTableChange(tableData) {
      // find the correct table
      const targetTable = this.formData[tableData.tableName];
      // find the correct row
      const targetRow = targetTable[tableData.rowIndex];
      // update the correct column
      targetRow[tableData.columnName] = tableData.value.value;
      this.unsavedChanges = true;
    },
    handlesAddRow(tableData) {
      // find the correct table
      const targetTable = this.formData[tableData.tableName];
      // add a new row
      targetTable.push(tableData.row);
      this.unsavedChanges = true;
    },
    handlesRemoveRow({ tableName, rowId }) {
      const targetTable = this.formData[tableName];
      targetTable.splice(rowId, 1);
      this.unsavedChanges = true;
    },
    handleDiscussionChange(answersData, answersObjectName) {
      this.formData[answersObjectName][answersData.questionIndex].answer =
        answersData.answer;
      this.unsavedChanges = true;
    }
  },
  watch: {
    getForm() {
      this.setForm();
    }
  },
  mounted() {
    this.setForm();
  }
};
</script>
